<script setup lang="ts">
import BannerAnnouncer from '@/components/banners/BannerAnnouncer.vue'
import EffectsBanner from '@/components/banners/EffectsBanner.vue'
import ErrorBanner from '@/components/banners/ErrorBanner.vue'
import SuccessBanner from '@/components/banners/SuccessBanner.vue'
import WarningBanner from '@/components/banners/WarningBanner.vue'
import UIMapBannerContainer from '@/components/ui/UIMapBannerContainer.vue'
import { useBannerStore } from '@/store/banner'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, ref, watch } from 'vue'

const bannerStore = useBannerStore()
const {
  warningText,
  warningVariant,
  errorText,
  successText,
  newMappedRemedy,
  oldMappedRemedy,
  withinPolygonChanged,
  flashQueue,
  flashCount
} = storeToRefs(bannerStore)
const show = ref(false)

let hideBannerTimeout: ReturnType<typeof setTimeout> | null = null
const clearBannerTimeout = () => {
  if (hideBannerTimeout) {
    clearTimeout(hideBannerTimeout)
    hideBannerTimeout = null
  }
}

const flashBannerVisibility = () => {
  clearBannerTimeout()
  show.value = true

  hideBannerTimeout = setTimeout(() => {
    show.value = false

    // extra timeout to allow for slideout animation before hiding with v-if statements
    hideBannerTimeout = setTimeout(() => {
      bannerStore.clearOldBanners()
      hideBannerTimeout = null
      bannerStore.flashNextInQueue()
    }, 500)
  }, 4000)
}

const flashBannerClasses = computed(
  () =>
    `flash-banner transition-[opacity] duration-300 ease-in-out ${
      show.value ? 'show-flash-banner opacity-100' : 'opacity-0'
    }`
)

watch(
  [
    warningText,
    errorText,
    successText,
    newMappedRemedy,
    oldMappedRemedy,
    flashCount
  ],
  () => {
    if (
      warningText.value ||
      errorText.value ||
      successText.value ||
      newMappedRemedy.value
    ) {
      flashBannerVisibility()
    }
  },
  {
    immediate: true
  }
)
watch(
  flashQueue,
  () => {
    bannerStore.flashNextInQueue()
  },
  { immediate: true, deep: true }
)

onBeforeUnmount(() => {
  clearBannerTimeout()
  bannerStore.clearOldBanners()
})
</script>

<template>
  <BannerAnnouncer />
  <UIMapBannerContainer variant="flash" class="flash-banners">
    <WarningBanner
      v-if="warningText"
      :banner-text="warningText"
      :banner-variant="warningVariant"
      :class="flashBannerClasses"
    />
    <ErrorBanner
      v-else-if="errorText"
      :banner-text="errorText"
      :class="flashBannerClasses"
    />
    <SuccessBanner
      v-else-if="successText"
      :banner-text="successText"
      :class="flashBannerClasses"
    />
    <EffectsBanner
      v-else-if="newMappedRemedy"
      :new-mapped-remedy="newMappedRemedy"
      :old-mapped-remedy="oldMappedRemedy"
      :added-to-sketch-site="
        withinPolygonChanged && !!newMappedRemedy.isWithinPolygon
      "
      :class="flashBannerClasses"
    />
  </UIMapBannerContainer>
</template>
